import L from 'leaflet';
import { DOPPLER_CONFIG } from '../config/dopplerRadar.config.js';
import { isQuadkeyTileUrl, xyzToQuadkey } from './dopplerTile.utils.js';

const QuadkeyTileLayer = L.TileLayer.extend({
  getTileUrl(coords) {
    const zoom = this._getZoomForUrl();
    const quadkey = xyzToQuadkey(coords.x, coords.y, zoom);

    return L.Util.template(this._url, {
      x: coords.x,
      y: coords.y,
      z: zoom,
      q: quadkey,
      quadkey,
      s: this._getSubdomain?.(coords) ?? '',
      r: L.Browser.retina ? '@2x' : '',
    });
  },
});

export const getDopplerFrameIndexes = (frameCount) =>
  Array.from({ length: Math.max(Number(frameCount) || 0, 0) }, (_, frameIndex) => frameIndex);

export const createDopplerRadarTileLayer = (url, options) =>
  isQuadkeyTileUrl(url) ? new QuadkeyTileLayer(url, options) : L.tileLayer(url, options);

export const getDopplerLayerVisualState = (frameIndex, currentFrame, trailFrame = null) => {
  if (frameIndex === currentFrame) {
    return { opacity: 1, zIndex: DOPPLER_CONFIG.currentZIndex };
  }

  if (frameIndex === trailFrame && trailFrame !== currentFrame) {
    return { opacity: DOPPLER_CONFIG.trailOpacity, zIndex: DOPPLER_CONFIG.trailZIndex };
  }

  return { opacity: 0, zIndex: DOPPLER_CONFIG.baseZIndex };
};

export const applyDopplerLayerVisualState = (layer, opacity, zIndex) => {
  if (!layer) return;

  layer.setOpacity(opacity);
  layer.setZIndex(zIndex);

  const container = layer.getContainer?.();
  if (!container) return;

  container.style.pointerEvents = 'none';
  container.style.willChange = 'opacity';
  container.style.transform = 'translate3d(0, 0, 0)';
  container.style.backfaceVisibility = 'hidden';
  container.style.display = opacity === 0 ? 'none' : '';
};

// Reloads only the tiles that failed. `layer.redraw()` would drop and re-request every tile in the
// frame. A tile Leaflet has since removed (pan, zoom) is skipped.
export const retryFailedDopplerTiles = (layer, failedTiles) => {
  failedTiles.forEach(({ tile, coords }) => {
    if (!tile?.isConnected || tile.getAttribute('src') === L.Util.emptyImageUrl) return;

    tile.src = layer.getTileUrl(coords);
  });
};

export const isVisibleDopplerLayerLoading = (
  layerLoadingState,
  currentFrame,
  trailFrame = null,
) => {
  const isCurrentFrameLoading = Boolean(layerLoadingState[currentFrame]);
  const isTrailFrameLoading =
    trailFrame !== null && trailFrame !== currentFrame && Boolean(layerLoadingState[trailFrame]);

  return isCurrentFrameLoading || isTrailFrameLoading;
};
